import { forwardRef, useCallback, useState } from "react";
import { StyleSheet, TextInput } from "react-native";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { Spacer } from "../Spacer";
import { View } from "../View";
import { BodyText } from "./Text/BodyText";
import type { TextInputProps } from "react-native";

const S = StyleSheet.create({
  multilineTextInput: {
    height: 180,
    paddingTop: styleConstantsV2.spacing16,
    paddingLeft: styleConstantsV2.spacing16,
    paddingRight: styleConstantsV2.spacing8,
    paddingBottom: styleConstantsV2.spacing24,
    fontFamily: "DMSans-Regular",
    fontSize: styleConstantsV2.fontSizeBodyLarge,
    lineHeight: 24,
    borderWidth: 2,
    borderRadius: styleConstantsV2.borderRadius8,
  },
  textLengthCounter: {
    position: "absolute",
    right: styleConstantsV2.spacing8,
    bottom: styleConstantsV2.spacing8,
  },
  helperText: {
    paddingLeft: styleConstantsV2.spacing16,
  },
});

export interface MultilineTextInputProps
  extends React.PropsWithChildren<TextInputProps> {
  isError?: boolean;
  disabled?: boolean;
  helperText?: React.ReactNode;
}

export const MultilineTextInput: React.ForwardRefExoticComponent<MultilineTextInputProps> =
  forwardRef<TextInput, MultilineTextInputProps>(
    (
      {
        value = "",
        onChangeText,
        maxLength,
        placeholder,
        disabled = false,
        isError = false,
        helperText,
        ...props
      },
      ref,
    ) => {
      const [isFocused, setIsFocused] = useState(false);
      const isEmpty = !value?.length;

      const resolveBackgroundColor = useCallback(() => {
        if (disabled) {
          return styleConstantsV2.colorGrey20;
        } else if (isError) {
          return styleConstantsV2.colorErrorBg;
        }

        return "transparent";
      }, [disabled, isError]);

      const resolveBorderColor = useCallback(() => {
        if (disabled) {
          return styleConstantsV2.colorGrey30;
        } else if (isError) {
          return styleConstantsV2.colorError;
        } else if (isFocused) {
          return styleConstantsV2.colorPrimaryMid;
        } else if (isEmpty) {
          return styleConstantsV2.colorGrey20;
        }

        return styleConstantsV2.colorBlack;
      }, [disabled, isFocused, isError, isEmpty]);

      const resolveFontColor = useCallback(() => {
        if (disabled) {
          return styleConstantsV2.colorGrey50;
        }

        return styleConstantsV2.colorBlack;
      }, [disabled]);

      const resolvePlaceholderColor = useCallback(() => {
        if (disabled) {
          return styleConstantsV2.colorGrey50;
        } else if (isError) {
          return styleConstantsV2.colorError;
        }

        return styleConstantsV2.colorGrey;
      }, [disabled, isError]);

      return (
        <View>
          <View>
            <TextInput
              ref={ref}
              value={value}
              onChangeText={onChangeText}
              multiline
              maxLength={maxLength}
              placeholder={placeholder}
              placeholderTextColor={resolvePlaceholderColor()}
              editable={!disabled}
              allowFontScaling={false}
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect={false}
              style={[
                S.multilineTextInput,
                {
                  outline: "none",
                  borderColor: resolveBorderColor(),
                  color: resolveFontColor(),
                  backgroundColor: resolveBackgroundColor(),
                },
              ]}
              onBlur={(e) => {
                props.onBlur?.(e);
                setIsFocused(false);
              }}
              onFocus={(e) => {
                props.onFocus?.(e);
                setIsFocused(true);
              }}
              {...props}
            />

            {maxLength && (
              <BodyText
                style={S.textLengthCounter}
                sizeSmall
                weightRegular
                colorGrey
              >
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                {value?.length}/{maxLength}
              </BodyText>
            )}
          </View>
          {helperText && (
            <View alignSelfFlexStart>
              <Spacer small />
              <View style={S.helperText}>{helperText}</View>
            </View>
          )}
        </View>
      );
    },
  );
